import { useState } from "react";
import { Check } from "lucide-react";
import { BUTTON, CHOSEN, KIT } from "./kit-tokens";

export type ChoiceOption = { label: string; value: string };

/**
 * C21: the assistant asked a question with a few answers (and C16, the
 * off-topic refusal, which offers two travel questions the same way).
 *
 * Chips are allowed here and only here (owner rule: chips only when the AI
 * asks, or when it turns down an off-topic question). One tap sends the
 * option's own words, already in the chat's language, as the guest's next
 * message; the other options fade and lock so the answer reads clearly.
 */
export function ChoiceAsk({
  question,
  options,
  onSend,
  locked = false,
  showQuestion = true,
}: {
  question?: string;
  options: ChoiceOption[];
  onSend: (text: string) => void;
  /** A later turn already answered it (e.g. reloaded history): nothing to tap. */
  locked?: boolean;
  /** The turn's prose already says the question; hide the copy here. */
  showQuestion?: boolean;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  if (options.length === 0) return null;
  const done = locked || picked !== null;

  return (
    <div className="flex flex-col gap-2" data-testid="kit-choice">
      {showQuestion && question ? (
        <p className="text-[15px] leading-[22px]" style={{ color: KIT.navy }}>
          {question}
        </p>
      ) : null}
      <div role="group" aria-label={question} className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const on = picked === o.value;
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={on}
              disabled={done && !on}
              onClick={() => {
                if (done) return;
                setPicked(o.value);
                onSend(o.value);
              }}
              className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold transition-opacity duration-200 disabled:opacity-40"
              style={on ? CHOSEN : { ...BUTTON, color: KIT.navy }}
            >
              {on ? <Check size={13} color={KIT.gold} strokeWidth={3} aria-hidden /> : null}
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
